'use client';

import { useTranslations } from 'next-intl';
import { ViduSLogo } from '@/app/components/icons/ViduSLogo';
import { ClientLink } from '@/app/components/shared/ClientLink';
import { buildEditingModeHref, COPY_RIGHT, SHENGSHU_WEBSITE_URL } from '@/app/constants';

const FOOTER_GROUPS = [
  {
    id: 'interaction',
    labelKey: 'interaction.label',
    items: [
      { href: '/vidu-stream/avatar', labelKey: 'interaction.onlineAvatar' },
      // 离线数字人入口暂时下线，后续恢复时取消注释。
      // { href: '/vidu-stream/avatar/offline', labelKey: 'interaction.offlineAvatar' },
    ],
  },
  {
    id: 'editing',
    labelKey: 'editing.label',
    items: [
      { href: buildEditingModeHref('style-rendering'), labelKey: 'editing.styleRendering' },
      { href: buildEditingModeHref('character-replacement'), labelKey: 'editing.characterReplacement' },
      { href: buildEditingModeHref('background-replacement'), labelKey: 'editing.backgroundReplacement' },
      { href: buildEditingModeHref('virtual-try-on'), labelKey: 'editing.virtualTryOn' },
    ],
  },
] as const;

export const ViduStreamFooter = () => {
  const t = useTranslations('viduStreamNavigation');
  const tHomeFooter = useTranslations('home3.footer');
  const tViduS = useTranslations('vidu-s');
  const contactText = tHomeFooter('company.contact').replace('support@vidu.com', 'vidus@shengshu.ai');
  const informationalGroups = [
    {
      id: 'company',
      title: tHomeFooter('company.title'),
      items: [
        { href: SHENGSHU_WEBSITE_URL.zh, label: tHomeFooter('company.about') },
        { href: 'mailto:vidus@shengshu.ai', label: contactText },
      ],
    },
  ];

  return (
    <footer className="mt-10 bg-[var(--s-page-bg)] px-[4.5%] pt-20 pb-8 text-[#121826] sm:px-6 sm:pt-14 sm:pb-6">
      <div className="border-[#121826]/10 border-t pt-12 sm:pt-9">
        <div className="flex items-start justify-between gap-16 sm:flex-col sm:gap-10">
          <div className="max-w-[360px]">
            <div className="flex items-center gap-4">
              <ClientLink
                aria-label={t('product')}
                className="inline-flex text-[#121826] transition-opacity hover:opacity-75"
                href="/vidu-stream"
              >
                <ViduSLogo aria-hidden="true" className="h-10 w-auto sm:h-8" />
              </ClientLink>
              {/* 小程序二维码暂时隐藏，后续恢复时取消注释。
              {SITE === 'china' ? (
                <span aria-label={t('miniProgram')} className="relative block size-[88px] rounded-8" role="img">
                  <img
                    alt=""
                    aria-hidden="true"
                    className="size-full rounded-8 object-contain"
                    src="/wechat_minicode_transparent.png"
                  />
                </span>
              ) : null} */}
            </div>
            <p className="mt-5 max-w-[320px] text-[#121826]/48 text-[14px] leading-6">{tViduS('statement')}</p>
          </div>

          <div className="flex flex-1 flex-nowrap justify-end gap-x-10 sm:w-full sm:flex-col sm:gap-8">
            {FOOTER_GROUPS.map((group) => (
              <div className="min-w-[130px]" key={group.id}>
                <h2 className="font-medium text-[#121826] text-[14px]">{t(group.labelKey)}</h2>
                <div className="mt-5 flex flex-col gap-3">
                  {group.items.map((item) => (
                    <ClientLink
                      className="w-fit text-[#121826]/60 text-[14px] transition-colors hover:text-[#121826]"
                      href={item.href}
                      key={item.href}
                    >
                      {t(item.labelKey)}
                    </ClientLink>
                  ))}
                </div>
              </div>
            ))}

            {informationalGroups.map((group) => (
              <div className="min-w-[130px]" key={group.id}>
                <h2 className="font-medium text-[#121826] text-[14px]">{group.title}</h2>
                <div className="mt-5 flex flex-col gap-3">
                  {group.items.map((item) => (
                    <ClientLink
                      className="w-fit text-[#121826]/60 text-[14px] transition-colors hover:text-[#121826]"
                      href={item.href}
                      key={item.href}
                      rel="noreferrer nofollow"
                      target={item.href.startsWith('/help-center') ? undefined : '_blank'}
                    >
                      {item.label}
                    </ClientLink>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-14 flex items-center justify-between gap-6 border-[#121826]/10 border-t pt-6 text-[#121826]/40 text-[12px] sm:mt-10 sm:flex-col sm:items-start sm:gap-3">
          <span>{COPY_RIGHT}</span>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            <ClientLink className="transition-colors hover:text-[#121826]" href="/terms" target="_blank">
              {tHomeFooter('terms')}
            </ClientLink>
            <ClientLink className="transition-colors hover:text-[#121826]" href="/privacy" target="_blank">
              {tHomeFooter('privacy')}
            </ClientLink>
          </div>
        </div>
      </div>
    </footer>
  );
};
